// src/v3/ui/scheduler/scheduler.hooks.ts
//
// Scheduler server-state hooks built on TanStack Query.
// All reads go through useApiQuery, all mutations through useApiMutation.
// Query keys from the single registry (qk.scheduler.*).
// No direct apiFetch/fetch calls — HTTP is handled by _common wrappers.

import type { NotificationConfig } from "@signals/shared";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { z } from "zod";
import { qk, useApiMutation, useApiQuery } from "../_common";
import {
  ASYNC_OPERATION_STATUS_OBJ,
  JOB_LOG_STATUS,
  JOB_PARAM_TYPE,
  JOB_STATUS,
  JOB_STATUS_OBJ,
  type TNotificationChannel,
} from "../_deps";

export type { TJobStatus as JobStatus } from "@signals/shared";

// ─── Schemas (type definitions; runtime validation is on the backend) ──────

const jobParamFieldSchema = z.object({
  name: z.string().min(1),
  type: z.enum(JOB_PARAM_TYPE),
  required: z.boolean(),
  options: z.array(z.string()).optional(),
  description: z.string().optional(),
});

const jobInfoSchema = z.object({
  name: z.string().min(1),
  status: z.enum(JOB_STATUS),
  dependencies: z.array(z.string()),
  hasParams: z.boolean().default(false),
  paramsFields: z.array(jobParamFieldSchema).default([]),
  scheduleName: z.string().default("daily"),
  lastRun: z
    .object({
      id: z.string(),
      status: z.string(),
      startedAt: z.union([z.string(), z.date()]).nullable(),
      finishedAt: z.union([z.string(), z.date()]).nullable(),
      result: z.string().optional(),
    })
    .nullable()
    .default(null),
});

const jobLogRowSchema = z.object({
  id: z.string().uuid(),
  jobName: z.string().min(1),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  status: z.enum(JOB_LOG_STATUS),
  result: z.string().default(""),
});

export type JobInfo = z.infer<typeof jobInfoSchema>;
export type JobLogRow = z.infer<typeof jobLogRowSchema>;

// ─── Response types ─────────────────────────────────────────────────────────

const jobsResponseSchema = z.object({ items: z.array(jobInfoSchema) });
const historyResponseSchema = z.object({
  items: z.array(jobLogRowSchema),
  total: z.number(),
});
const cronResponseSchema = z.object({ cronExpression: z.string() });
const updateCronResponseSchema = z.object({
  cronExpression: z.string(),
  message: z.string().optional(),
});
const runJobResponseSchema = z.object({
  status: z.string(),
  logId: z.string().nullable(),
  job: z.string(),
});
const testNotificationResponseSchema = z.object({ status: z.string() });

type JobsResponse = z.infer<typeof jobsResponseSchema>;
type HistoryResponse = z.infer<typeof historyResponseSchema>;
type CronResponse = z.infer<typeof cronResponseSchema>;
type UpdateCronResponse = z.infer<typeof updateCronResponseSchema>;
type RunJobResponse = z.infer<typeof runJobResponseSchema>;
type TestNotificationResponse = z.infer<typeof testNotificationResponseSchema>;

// ─── Polling ────────────────────────────────────────────────────────────────

/**
 * Poll job status until it's no longer "running" (or max attempts reached).
 * Uses queryClient.refetchQueries (no direct fetch) to refresh the jobs list,
 * then reads the cached result to check the job status.
 * Stop conditions: job status is "completed" or "failed", or 200 attempts (~10 min).
 */
async function pollJobStatus(
  queryClient: QueryClient,
  jobName: string,
  intervalMs = 3000,
  maxAttempts = 200,
): Promise<void> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    await queryClient.refetchQueries({ queryKey: qk.scheduler.jobs });
    const data = queryClient.getQueryData<JobsResponse>(qk.scheduler.jobs);
    const job = data?.items.find((j) => j.name === jobName);
    if (job && job.status !== JOB_STATUS_OBJ.RUNNING) return;
  }
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch the list of scheduler jobs.
 * Query key: qk.scheduler.jobs.
 */
export function useSchedulerJobs() {
  const query = useApiQuery<JobsResponse>(
    qk.scheduler.jobs,
    "/api/scheduler/jobs",
  );

  return {
    jobs: query.data?.items ?? [],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

/**
 * Fetch scheduler job logs (execution history) with pagination + optional filter.
 * Query key: qk.scheduler.history(filters) — auto-refetches when params change.
 */
export function useSchedulerJobLogs(params: {
  limit: number;
  offset: number;
  jobName?: string;
}) {
  const searchParams = new URLSearchParams();
  searchParams.set("limit", String(params.limit));
  searchParams.set("offset", String(params.offset));
  if (params.jobName) searchParams.set("jobName", params.jobName);
  const url = `/api/scheduler/history?${searchParams.toString()}`;

  const query = useApiQuery<HistoryResponse>(qk.scheduler.history(params), url);

  return {
    logs: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

/**
 * Run a scheduler job by name (optionally with params).
 * Mutation: POST /api/scheduler/jobs/:name/run.
 * Invalidation: qk.scheduler.all (covers jobs + history).
 * If the backend returns 202 "started", polls job status until completion.
 */
export function useRunJob() {
  const queryClient = useQueryClient();
  const mutation = useApiMutation<
    { name: string; params?: Record<string, unknown> },
    RunJobResponse
  >(
    (body) => `/api/scheduler/jobs/${encodeURIComponent(body.name)}/run`,
    "POST",
    {
      invalidateKeys: [qk.scheduler.all],
      onSuccess: (data, variables) => {
        if (data.status === ASYNC_OPERATION_STATUS_OBJ.STARTED) {
          void pollJobStatus(queryClient, variables.name);
        }
      },
    },
  );

  const runJob = useCallback(
    async (name: string, params?: Record<string, unknown>) => {
      await mutation.mutateAsync({
        name,
        ...(params ? { params } : {}),
      });
    },
    [mutation],
  );

  return {
    runJob,
    isPending: mutation.isPending,
    error: mutation.error?.message ?? null,
  };
}

/**
 * Read + update the daily pipeline cron expression.
 * Query key: qk.scheduler.cron.
 * Mutation: PUT /api/scheduler/cron — invalidates qk.scheduler.cron.
 */
export function useSchedulerCron() {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const query = useApiQuery<CronResponse>(
    qk.scheduler.cron,
    "/api/scheduler/cron",
  );

  const mutation = useApiMutation<
    { cronExpression: string },
    UpdateCronResponse
  >("/api/scheduler/cron", "PUT", {
    invalidateKeys: [qk.scheduler.cron],
    onSuccess: (data) => {
      setSuccessMessage(data.message ?? "Cron updated");
      setMutationError(null);
    },
    onError: (err) => {
      setMutationError(err.message);
      setSuccessMessage(null);
    },
  });

  const updateCron = useCallback(
    async (newExpression: string) => {
      try {
        await mutation.mutateAsync({ cronExpression: newExpression });
      } catch {
        // error already captured in onError
      }
    },
    [mutation],
  );

  const clearMessages = useCallback(() => {
    setMutationError(null);
    setSuccessMessage(null);
  }, []);

  return {
    cronExpression: query.data?.cronExpression ?? "",
    isLoading: query.isLoading || mutation.isPending,
    error: mutationError ?? (query.error ? query.error.message : null),
    successMessage,
    updateCron,
    clearMessages,
  };
}

/**
 * Read + update the symbols-refresh cron expression.
 * Query key: qk.scheduler.symbolsCron.
 * Mutation: PUT /api/scheduler/symbols-cron — invalidates qk.scheduler.symbolsCron.
 */
export function useSchedulerSymbolsCron() {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const query = useApiQuery<CronResponse>(
    qk.scheduler.symbolsCron,
    "/api/scheduler/symbols-cron",
  );

  const mutation = useApiMutation<
    { cronExpression: string },
    UpdateCronResponse
  >("/api/scheduler/symbols-cron", "PUT", {
    invalidateKeys: [qk.scheduler.symbolsCron],
    onSuccess: (data) => {
      setSuccessMessage(data.message ?? "Symbols cron updated");
      setMutationError(null);
    },
    onError: (err) => {
      setMutationError(err.message);
      setSuccessMessage(null);
    },
  });

  const updateCron = useCallback(
    async (newExpression: string) => {
      try {
        await mutation.mutateAsync({ cronExpression: newExpression });
      } catch {
        // error already captured in onError
      }
    },
    [mutation],
  );

  const clearMessages = useCallback(() => {
    setMutationError(null);
    setSuccessMessage(null);
  }, []);

  return {
    cronExpression: query.data?.cronExpression ?? "",
    isLoading: query.isLoading || mutation.isPending,
    error: mutationError ?? (query.error ? query.error.message : null),
    successMessage,
    updateCron,
    clearMessages,
  };
}

// ─── Notifications ──────────────────────────────────────────────────────────

export type { NotificationConfig } from "@signals/shared";
export type NotificationChannel = TNotificationChannel;

/**
 * Read + update scheduler failure-notification settings.
 * GET returns masked secrets ("****"); the form echoes the mask back and
 * the backend preserves the stored value.
 */
export function useSchedulerNotifications() {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const query = useApiQuery<NotificationConfig>(
    qk.scheduler.notifications,
    "/api/scheduler/notifications",
  );

  const mutation = useApiMutation<NotificationConfig, NotificationConfig>(
    "/api/scheduler/notifications",
    "PUT",
    {
      invalidateKeys: [qk.scheduler.notifications],
      onSuccess: () => {
        setSuccessMessage("Notification settings saved");
        setMutationError(null);
      },
      onError: (err) => {
        setMutationError(err.message);
        setSuccessMessage(null);
      },
    },
  );

  const testMutation = useApiMutation<
    NotificationConfig,
    TestNotificationResponse
  >("/api/scheduler/notifications/test", "POST", {
    onSuccess: () => {
      setSuccessMessage("Test notification sent");
      setMutationError(null);
    },
    onError: (err) => {
      setMutationError(err.message);
      setSuccessMessage(null);
    },
  });

  const saveConfig = useCallback(
    async (config: NotificationConfig) => {
      try {
        await mutation.mutateAsync(config);
      } catch {
        // error already captured in onError
      }
    },
    [mutation],
  );

  const testConfig = useCallback(
    async (config: NotificationConfig) => {
      try {
        await testMutation.mutateAsync(config);
      } catch {
        // error already captured in onError
      }
    },
    [testMutation],
  );

  const clearMessages = useCallback(() => {
    setMutationError(null);
    setSuccessMessage(null);
  }, []);

  return {
    config: query.data ?? null,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    isTesting: testMutation.isPending,
    error: mutationError ?? (query.error ? query.error.message : null),
    successMessage,
    saveConfig,
    testConfig,
    clearMessages,
  };
}
